import { Directive, ElementRef, HostListener, Input, NgZone, OnInit, Renderer2 } from '@angular/core';
import { fromEvent, Subject, takeUntil } from 'rxjs';

/**
 * Directive to make an element resizable. Resizable elements are denoted with the 'resize-handle' class.
 * Specifically, this is used to let users manually resize table headers. 
 */
@Directive({
  selector: '[resizable]',
  standalone: true
})
export class ResizableDirective implements OnInit {
  // Whether column need resizing or not
  @Input("resizable") resizable: boolean = false;

  /** Index of our resizable column, used to track row cells later */
  @Input("resizeIndex") index: number = 0;

  /** HTML column element */
  private column: HTMLElement;

  /** Associated table element */
  private table!: HTMLElement;

  /** Starting X position of the user's mouse on resize */
  private startX!: number;

  /** Starting width of resizeable element */
  private startWidth!: number;

  /** Status on if user is resizing element */
  private isResizing = false;

  /** Resizer element */
  private resizer!: HTMLElement;

  /** Subject to destroy resizer when done */
  private destroy$ = new Subject<void>();

  /**
   * Default constructor to initialize attributes
   * 
   * @param _ref Reference of the element attached to the directive
   * @param _renderer Renderer to manipulate the element
   * @param _zone Zone to run outside Angular
   */
  constructor(
    private _ref: ElementRef,
    private _renderer: Renderer2,
    private _zone: NgZone
  ) {
    this.column = this._ref.nativeElement;
  }

  /**
   * On initialization, create the resizer element and initialize the resize listener
   */
  ngOnInit() {
    if (this.resizable) {
      // Find the table associated with the provided resizable column
      const row = this._renderer.parentNode(this.column);
      const thead = this._renderer.parentNode(row);
      this.table = this._renderer.parentNode(thead);

      // Create the resizer element
      this.resizer = this._renderer.createElement('span');
      this._renderer.addClass(this.resizer, 'column-resizer');
      this._renderer.appendChild(this.column, this.resizer);

      this._zone.runOutsideAngular(() => {
        // Listen for mouse down event until destroyed
        fromEvent(this.resizer, 'mousedown')
          .pipe(takeUntil(this.destroy$))
          .subscribe((event: Event) => this.onMouseDown(event as MouseEvent));

        // Listen for mouse move event until destroyed
        fromEvent(document, 'mousemove')
          .pipe(takeUntil(this.destroy$))
          .subscribe((event: Event) => this.onMouseMove(event as MouseEvent));

        // Listen for mouse up event until destroyed
        fromEvent(document, 'mouseup')
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => this.onMouseUp());
      });
    }
  }

  /**
   * On mouse down, set the starting X position and width of the column 
   * 
   * @param event 
   */
  private onMouseDown(event: MouseEvent) {
    event.preventDefault();

    this.isResizing = true;
    this.startX = event.pageX;
    this.startWidth = this.column.offsetWidth + paddingToNumber(this.column.style.paddingLeft) + paddingToNumber(this.column.style.paddingRight);
  }

  /**
   * On mouse move, resize the column based on the user's mouse movement 
   * 
   * @param event 
   */
  private onMouseMove(event: MouseEvent) {
    if (!this.isResizing) return; // We don't care what's happening if we're not actively resizing

    // Calculate new width
    const width = max(this.startWidth + (event.pageX - this.startX), 0);

    // Update oclumn width
    this._renderer.setStyle(this.column, 'width', `${width}px`);
    this._renderer.setStyle(this.column, 'min-width', `${width}px`);

    // Set table cells width
    const tableCells = Array.from(this.table.querySelectorAll("mat-row")).map((row: any) => row.querySelectorAll("mat-cell").item(this.index));
    for (const cell of tableCells) {
      this._renderer.setStyle(cell, "width", `${width}px`);
      this._renderer.setStyle(cell, 'min-width', `${width}px`);
    }
  }

  /**
   * On mouse up, stop resizing the column 
   */
  private onMouseUp() {
    if (!this.isResizing) return; // We don't care what's happening if we're not actively resizing

    this.isResizing = false;
  }

  /**
   * When the directive is destroyed, complete the destroy subject to stop event listeners
   */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

// Helper functions
const max = (a: number, b: number): number => {
  return a > b ? a : b;
}

const paddingToNumber = (value: string | null): number  => {
  return value ? parseInt(value.replace('px', '')) : 0;
}
